/* eslint-disable no-unused-expressions */
import React from 'react'
import { AnimatePresence, motion } from 'framer-motion'

type ItemVariant = 'default' | 'error'

interface IDropdownItem {
  title: string
  onClick: () => void
  variant?: ItemVariant
}

interface IDropdownProps extends React.HTMLAttributes<HTMLDivElement> {
  items: IDropdownItem[]
  children: React.ReactElement
}

const Dropdown: React.FC<IDropdownProps & any> = ({
  children,
  items,
  ...rest
}) => {
  const [activeItemIndex, setActiveItemIndex] = React.useState<number>(-1)
  const [isOpen, setIsOpen] = React.useState<boolean>(false)

  const node = React.useRef<HTMLDivElement>(null)

  const handleKeyDown = React.useCallback((e: any) => {
    switch (e.keyCode) {
      case 38:
        e.preventDefault()
        setActiveItemIndex((index) => {
          return !index ? 0 : index - 1
        })
        break
      case 40:
        e.preventDefault()
        setActiveItemIndex((index) => {
          return index + 1 === items.length ? index : index + 1
        })
        break
    }
  }, [])

  const handleClick = React.useCallback((e: any) => {
    if (!node.current || node.current.contains(e.target)) {
      return
    }

    setIsOpen(false)
  }, [])

  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleClick)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('click', handleClick)
    }
  }, [handleKeyDown, handleClick])

  return (
    <div
      ref={node}
      className='dropdown-wrapper'
      tabIndex={-1}
      onKeyDown={(e) => {
        switch (e.keyCode) {
          case 27:
            setIsOpen(false)
            break
          case 13:
            e.preventDefault()
            if (isOpen) {
              items[activeItemIndex].onClick()
              setIsOpen(false)
            } else {
              setIsOpen(true)
            }
        }
      }}
    >
      <AnimatePresence>
        {isOpen && (
          <motion.div
            transition={{ ease: 'easeOut' }}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className='dropdown-menu'
            {...rest}
          >
            {items.map((item: IDropdownItem, i: number) => (
              <div
                key={item.title}
                tabIndex={0}
                className={`dropdown-item dropdown-item-${item.variant} ${
                  activeItemIndex === i ? 'dropdown-item-active' : ''
                }`}
                role='button'
                onKeyDown={(e) => {
                  if (e.keyCode === 13) {
                    item.onClick()
                    setIsOpen(false)
                  }
                }}
                onClick={() => {
                  item.onClick()
                  setIsOpen(false)
                }}
              >
                {item.title}
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <div onClick={() => setIsOpen(true)}>{children}</div>
    </div>
  )
}

export default Dropdown
